import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Send, CheckCircle, AlertCircle, Camera, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const MeuTrampoAvaliar = () => {
  const { slug, code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [professional, setProfessional] = useState(null);
  const [service, setService] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    text: '',
    rating: 5,
    photo: null,
    service: ''
  });

  useEffect(() => {
    const validateLink = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/portfolio/evaluation/${code}`);
        
        if (!response.ok) {
          const data = await response.json();
          setError(data.detail || 'Link inválido ou já utilizado');
          setLoading(false);
          return;
        }
        
        const data = await response.json();
        setProfessional(data.professional);
        setService(data.service || '');
        setFormData(prev => ({ ...prev, service: data.service || '' }));
        setLoading(false);
      } catch (e) {
        setError('Erro ao validar link');
        setLoading(false);
      }
    };

    validateLink();
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/portfolio/evaluation/${code}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao enviar avaliação');
      }
      
      setSuccess(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">Link inválido</h1>
        <p className="text-slate-600 text-center mb-6">{error}</p>
        <Button onClick={() => navigate('/')} variant="outline">
          Voltar para início
        </Button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-purple-600 to-indigo-700">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Obrigado!</h1>
          <p className="text-slate-600 mb-6">
            Sua avaliação foi enviada com sucesso para {professional?.business || professional?.name}.
          </p>
          <Button 
            onClick={() => navigate(`/meutrampo/${slug}`)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Ver portfólio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-700 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Deixe sua avaliação</h1>
          <p className="text-purple-100">
            Sua opinião é muito importante para {professional?.business || professional?.name}
          </p>
        </div>

        {/* Professional Card */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-6 flex items-center gap-4">
          {professional?.photo ? (
            <img 
              src={professional.photo} 
              alt={professional.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-2xl text-white">{professional?.name?.[0]}</span>
            </div>
          )}
          <div>
            <h2 className="font-semibold text-white">{professional?.business || professional?.name}</h2>
            {service && <p className="text-purple-200 text-sm">Serviço: {service}</p>}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Rating */}
          <div className="mb-6">
            <Label className="mb-3 block">Como foi sua experiência?</Label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star 
                    size={36} 
                    className={star <= formData.rating 
                      ? 'fill-yellow-400 text-yellow-400' 
                      : 'text-slate-300'
                    }
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-slate-500 mt-2">
              {formData.rating === 5 && 'Excelente!'}
              {formData.rating === 4 && 'Muito bom!'}
              {formData.rating === 3 && 'Bom'}
              {formData.rating === 2 && 'Regular'}
              {formData.rating === 1 && 'Ruim'}
            </p>
          </div>

          {/* Name */}
          <div className="mb-4">
            <Label htmlFor="name">Seu nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Como você quer ser identificado"
              required
              className="mt-2"
            />
          </div>

          {/* Service (if not predefined) */}
          {!service && (
            <div className="mb-4">
              <Label htmlFor="service">Qual serviço foi realizado?</Label>
              <Input
                id="service"
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                placeholder="Ex: Reforma do banheiro"
                className="mt-2"
              />
            </div>
          )}

          {/* Text */}
          <div className="mb-6">
            <Label htmlFor="text">Conte sua experiência</Label>
            <textarea
              id="text"
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="O que você achou do trabalho? O profissional foi pontual? Recomendaria?"
              required
              rows={4}
              className="w-full mt-2 p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={submitting || !formData.name || !formData.text}
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send size={18} />
                Enviar avaliação
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-purple-200 text-xs mt-6">
          Sua avaliação será publicada no portfólio de {professional?.business || professional?.name}
        </p>
      </div>
    </div>
  );
};

export default MeuTrampoAvaliar;
